import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import { FormattedMessage } from "react-intl";
import {
  GetLicenseManagementCustomers,
  GetLicenseManagementCourses,
} from "../../actions/superUserQueries";
import LicMgtCustomerModal from "../../components/Modal/LicMgtCustomerModal";
import LicMgtHandleCoursesModal from "../../components/Modal/LicMgtHandleCoursesModal";
import LicMgtCreateCustomerModal from "../../components/Modal/LicMgtCreateCustomerModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { diffMonths, formatDate } from "../../Utilities/dateUtils";
import { WarnLimitForLicMgt } from "../../constants/ConstantVars";
import { useIntl } from "react-intl";
import DataGrid from "../../components/DataGrids/DataGrid";

function LicMgtAdministration() {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState("");
  const [customerRows, setCustomerRows] = useState([]);
  const [openCustomer, setOpenCustomer] = useState(null);
  const [createNewCustomerOpen, setCreateNewCustomerOpen] = useState(false);
  const [toggleUpdate, setToggleUpdate] = useState(false);
  const [coursesModalOpen, setCoursesModalOpen] = useState(false);
  const licenseCourses = GetLicenseManagementCourses();

  const licenseCustomers = GetLicenseManagementCustomers();

  const compareStatusPriority = (rowA, rowB, columnId) => {
    const priorityA = rowA.original[columnId]?.props?.sortorder ?? 99;
    const priorityB = rowB.original[columnId]?.props?.sortorder ?? 99;

    if (priorityA === priorityB) return 0;

    return priorityA < priorityB ? -1 : 1;
  };

  const columns = [
    {
      id: "name",
      key: "name",
      header: <FormattedMessage id="Name" />,
      width: 150,
    },
    {
      id: "url",
      key: "url",
      header: <FormattedMessage id="Url" />,
      width: 170,
    },
    {
      id: "fortNoxNumber",
      key: "fortNoxNumber",
      header: <FormattedMessage id="FortnoxNumber" />,
      width: 170,
    },
    {
      id: "address",
      key: "address",
      header: <FormattedMessage id="Address" />,
      width: 210,
    },
    {
      id: "nbrOfLicenses",
      key: "nbrOfLicenses",
      header: <FormattedMessage id="NumberOfLicenses" />,
      width: 140,
      headerClassName: "justify-content-end",
      cellClassName: "text-end",
    },
    {
      id: "nbrOfValidLicenses",
      key: "nbrOfValidLicenses",
      header: <FormattedMessage id="NumberOfValidLicenses" />,
      width: 190,
      headerClassName: "justify-content-end",
      cellClassName: "text-end",
    },
    {
      id: "nbrOfLicensesNotUsed",
      key: "nbrOfLicensesNotUsed",
      header: <FormattedMessage id="NumberOfUnusedLicenses" />,
      width: 190,
      headerClassName: "justify-content-end",
      cellClassName: "text-end",
    },
    {
      id: "created",
      key: "created",
      header: <FormattedMessage id="Created" />,
      width: 100,
    },
    {
      id: "status",
      key: "status",
      header: <FormattedMessage id="Status" />,
      width: 90,
      sortingFn: compareStatusPriority,
      cell: (props) => props.getValue(),
    },
  ];

  const getStatusIcon = useCallback(
    (company) => {
      const statuses = {
        noLicensesLeft: "noLicensesLeft",
        belowThresholdForWarnLeft: "belowThresholdForWarnLeft",
        ok: "ok",
        attentionNeeded: "attentionNeeded",
      };
      let currentStatus = statuses.ok;
      let coursesWithNoLicensesLeft = [];
      let coursesWithFewLicensesLeft = [];

      company.licensesPerCourse.forEach((course) => {
        if (course.nbrOfLicensesNotUsed === 0) {
          currentStatus = statuses.noLicensesLeft;
          coursesWithNoLicensesLeft.push(course.course.name);
        } else if (course.nbrOfLicensesNotUsed < WarnLimitForLicMgt + 1) {
          if (currentStatus !== statuses.noLicensesLeft) {
            currentStatus = statuses.belowThresholdForWarnLeft;
          }
          coursesWithFewLicensesLeft.push(course.course.name);
        }
      });

      if (
        company.licenses.length ===
          company.licenses.filter((li) => li.firstRun === null).length &&
        company.licenses.length > 0
      ) {
        if (
          diffMonths(
            company.licenses.sort(function (a, b) {
              return b.created.localeCompare(a.created);
            })[0].created, new Date()) < 0
        ) {
          if (currentStatus === statuses.ok) {
            currentStatus = statuses.attentionNeeded;
          }
        }
      }

      if (
        company.nbrOfLicenses === 0 &&
        company.nbrOfLicensesNotUsed === 0 &&
        diffMonths(company.customer.created, new Date()) < 0
      ) {
        if (currentStatus === statuses.ok) {
          currentStatus = statuses.attentionNeeded;
        }
      }

      let icon = "thumbs-up";
      let colorClass = "text-success";
      let sortOrder = 4;
      let tooltipText = intl.formatMessage({ id: "LicenseStatusOk" });

      switch (currentStatus) {
        case statuses.noLicensesLeft:
          icon = "triangle-exclamation";
          colorClass = "text-danger";
          sortOrder = 1;
          tooltipText = intl.formatMessage(
            { id: "NoUnusedLicensesLeftForCourses" },
            { courses: coursesWithNoLicensesLeft.join(", ") },
          );
          break;
        case statuses.belowThresholdForWarnLeft:
          icon = "triangle-exclamation";
          colorClass = "text-warning";
          sortOrder = 2;
          tooltipText = intl.formatMessage(
            { id: "FewUnusedLicensesLeftForCourses" },
            { courses: coursesWithFewLicensesLeft.join(", ") },
          );
          break;
        case statuses.attentionNeeded:
          colorClass = "text-warning";
          sortOrder = 3;
          tooltipText = intl.formatMessage({
            id: "LicensesUnchangedForOverAMonth",
          });
          break;
        default:
          break;
      }

      const tooltipId = `customerStatusTooltip-${company.customer.id}`;

      return (
        <span sortorder={sortOrder}>
          <FontAwesomeIcon id={tooltipId} icon={icon} className={colorClass} />
          <UncontrolledTooltip target={tooltipId} fade={false}>
            {tooltipText}
          </UncontrolledTooltip>
        </span>
      );
    },
    [intl]
  );

  useEffect(() => {
    if (!licenseCustomers.isPending) {
      const licenseManagementTableDataSource = [];
      licenseCustomers.data
        .filter((item) => {
          const search = searchValue.toLowerCase();
          const c = item.customer;
          return (
            c.name?.toLowerCase().includes(search) ||
            c.url?.toLowerCase().includes(search) ||
            c.fortNoxNumber?.toLowerCase().includes(search) ||
            c.address?.toLowerCase().includes(search) ||
            c.city?.toLowerCase().includes(search)
          );
        })
        .forEach((companyItem) => {
          let company = {
            uniqueId: companyItem.customer.id,
            name: companyItem.customer.name,
            url: companyItem.customer.url,
            fortNoxNumber: companyItem.customer.fortNoxNumber,
            address:
              companyItem.customer.address +
              " " +
              companyItem.customer.city +
              " " +
              companyItem.customer.zipCode,
            nbrOfLicenses: companyItem.nbrOfLicenses,
            nbrOfValidLicenses: companyItem.nbrOfValidLicenses,
            nbrOfLicensesNotUsed: companyItem.nbrOfLicensesNotUsed,
            created: formatDate(companyItem.customer.created, "P"),
            status: getStatusIcon(companyItem),
          };
          licenseManagementTableDataSource.push(company);
        });
      setCustomerRows(licenseManagementTableDataSource);
    }
  }, [
    licenseCustomers.isPending,
    licenseCustomers.data,
    searchValue,
    toggleUpdate,
    getStatusIcon,
  ]);

  const handleRowClick = useCallback(
    (row) => {
      setOpenCustomer(
        licenseCustomers.data.find(
          (item) => item.customer.id === row.original.uniqueId,
        ),
      );
    },
    [licenseCustomers.data],
  );

  if (licenseCustomers.isPending) {
    return <></>;
  }

  return (
    <Card>
      <CardBody>
        <CardTitle>
          <FormattedMessage id="Navigation_Administration_Licenses" />
        </CardTitle>

        <div className="card">
          <Row>
            <Col>
              <Button
                className="m-2"
                outline
                color="primary"
                onClick={() => setCreateNewCustomerOpen(true)}
              >
                <FontAwesomeIcon icon="plus" className="me-2" />
                <FormattedMessage id="AddCustomer" />
              </Button>
              <Button
                className="m-2"
                outline
                color="primary"
                onClick={() => setCoursesModalOpen(true)}
              >
                <FormattedMessage id="ManageCourses" />
              </Button>
            </Col>
            <Col xs="auto" className="d-flex align-items-center me-2">
              <Input
                type="text"
                placeholder={intl.formatMessage({ id: "Search" })}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Col>
          </Row>
          <DataGrid
            rows={customerRows}
            columns={columns}
            getRowId={(row) => row.uniqueId}
            onRowClick={handleRowClick}
            rowClassName="license-management-table--row"
            rowStyle={{ cursor: "pointer" }}
          />
        </div>
      </CardBody>
      <LicMgtCustomerModal
        toggle={() => setOpenCustomer(null)}
        isOpen={openCustomer !== null}
        customer={openCustomer}
        licenseCourses={licenseCourses}
        handleUpdateData={() => setToggleUpdate(!toggleUpdate)}
        toggleUpdate={toggleUpdate}
      />
      <LicMgtCreateCustomerModal
        toggle={() => setCreateNewCustomerOpen(false)}
        isOpen={createNewCustomerOpen}
        licenseCustomers={licenseCustomers}
      />
      <LicMgtHandleCoursesModal
        toggle={() => setCoursesModalOpen(!coursesModalOpen)}
        isOpen={coursesModalOpen}
      />
    </Card>
  );
}

export default LicMgtAdministration;
